import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";

/**
 * Inline rich-text field used inside text-bearing blocks (paragraph, list item,
 * quote body). Wraps a small Tiptap instance with only the marks we care about
 * for an editorial article — no headings/lists at this level (those are their
 * own blocks). Exposes a tiny floating bubble menu on selection.
 *
 * The HTML it produces is sanitised on the server before storage.
 */
export function InlineRichText({
  html,
  placeholder,
  onUpdate,
  autoFocus,
  multiline = false,
}: {
  html: string;
  placeholder?: string;
  onUpdate: (next: string) => void;
  autoFocus?: boolean;
  /** When false, Enter keys insert a soft break instead of a new paragraph. */
  multiline?: boolean;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: { keepMarks: true },
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: html || "",
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor }) => onUpdate(editor.getHTML()),
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (!multiline && event.key === "Enter" && !event.shiftKey) {
          // Suppress new paragraph creation on single-line fields
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  // Sync external html changes (rare — usually internal)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== html) {
      editor.commands.setContent(html || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html]);

  if (!editor) return null;

  return (
    <div className="inline-rt">
      <BubbleMenu editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tiny floating bubble menu — Bold, Italic, Link
// -----------------------------------------------------------------------------

import type { Editor } from "@tiptap/react";

function BubbleMenu({ editor }: { editor: Editor }) {
  const apply = (cmd: () => void) => {
    cmd();
  };

  return (
    <div className="inline-rt-menu" data-active={!editor.state.selection.empty} aria-hidden={editor.state.selection.empty}>
      <button
        type="button"
        className={`inline-rt-btn ${editor.isActive("bold") ? "is-active" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          apply(() => editor.chain().focus().toggleBold().run());
        }}
        title="Bold"
      >
        B
      </button>
      <button
        type="button"
        className={`inline-rt-btn ${editor.isActive("italic") ? "is-active" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          apply(() => editor.chain().focus().toggleItalic().run());
        }}
        title="Italic"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        className={`inline-rt-btn ${editor.isActive("code") ? "is-active" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          apply(() => editor.chain().focus().toggleCode().run());
        }}
        title="Inline code"
      >
        <code>{`</>`}</code>
      </button>
      <button
        type="button"
        className={`inline-rt-btn ${editor.isActive("link") ? "is-active" : ""}`}
        onMouseDown={(e) => {
          e.preventDefault();
          const prev = editor.getAttributes("link").href as string | undefined;
          const url = window.prompt("Link URL", prev ?? "https://");
          if (url === null) return;
          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
          } else {
            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          }
        }}
        title="Link"
      >
        🔗
      </button>
    </div>
  );
}
